/**
 * ChangesList rendering helpers — plate density without inventing oid / Jobs.
 * API shape stays pcbx.change/1.
 */

import { shortOid, type HardwareRevision } from "./api.ts";
import {
  checkConclusionLabel,
  reviewStatusLabel,
  type ChangeListItem,
  type CheckConclusion,
} from "./collaboration.ts";

export const JOBS_GATE_KINDS = ["erc", "drc", "bom"] as const;
export type JobsGateKind = (typeof JOBS_GATE_KINDS)[number];
export type JobsGateDotState = "pass" | "fail" | "pending" | "none";

export type JobsGateDot = {
  kind: JobsGateKind;
  state: JobsGateDotState;
};

export type ChangeStatusTone = "open" | "merged" | "closed" | "requested" | "approved" | "muted";

export type ChangeSnapshotOption = {
  snapshot: string;
  oid: string | null;
  label: string;
};

const OID_CHARS = 8;

function trimRef(value: string | null | undefined): string | null {
  const v = value?.trim();
  return v ? v : null;
}

/** Truncate a real oid or snapshot id. Empty → null (caller must not invent). */
export function truncateChangeRef(value: string | null | undefined, n = OID_CHARS): string | null {
  const v = trimRef(value);
  if (!v) return null;
  return v.length > n ? v.slice(0, n) : v;
}

/**
 * Mono baseline…candidate range. Prefer oid; fall back to snapshot id.
 * Never synthesizes a git oid.
 */
export function changeOidRange(item: {
  baseline_oid?: string | null;
  candidate_oid?: string | null;
  baseline_snapshot: string;
  candidate_snapshot: string;
}): string {
  const left = truncateChangeRef(item.baseline_oid) ?? truncateChangeRef(item.baseline_snapshot) ?? "—";
  const right = truncateChangeRef(item.candidate_oid) ?? truncateChangeRef(item.candidate_snapshot) ?? "—";
  return `${left}…${right}`;
}

export function changeTitle(item: Pick<ChangeListItem, "title" | "baseline_snapshot" | "candidate_snapshot">): string {
  const title = item.title?.trim();
  if (title) return title;
  return `${item.baseline_snapshot} → ${item.candidate_snapshot}`;
}

export function isOpenChangeStatus(status: string | null | undefined): boolean {
  return status === "open" || status === "changes_requested";
}

export function countOpenChanges(
  items: Array<{ review_status?: string | null; status?: string | null }>,
): number {
  return items.filter((item) => isOpenChangeStatus(item.review_status ?? item.status)).length;
}

export function changeStatusTone(status: string | null | undefined): ChangeStatusTone {
  if (status === "open") return "open";
  if (status === "merged") return "merged";
  if (status === "closed") return "closed";
  if (status === "changes_requested") return "requested";
  if (status === "approved") return "approved";
  return "muted";
}

export function changeStatusPillClass(tone: ChangeStatusTone): string {
  switch (tone) {
    case "open":
      return "border-[#3D9B6A] bg-transparent text-[#7DCEA0]";
    case "merged":
    case "approved":
      return "border-[var(--copper)] bg-transparent text-[var(--copper)]";
    case "requested":
      return "border-border bg-transparent text-foreground";
    case "closed":
    case "muted":
      return "border-border bg-transparent text-muted-foreground";
  }
}

/** Map a JobsChecks kind onto ERC/DRC/BOM only when the kind actually says so. */
export function jobsGateKindFromCheck(kind: string | null | undefined): JobsGateKind | null {
  const k = (kind ?? "").trim().toLowerCase();
  if (!k) return null;
  if (k === "erc" || k.includes("erc")) return "erc";
  if (k === "drc" || k.includes("drc")) return "drc";
  if (k === "bom" || k.includes("bom")) return "bom";
  return null;
}

export function jobsGateDotState(conclusion: CheckConclusion | string | null | undefined): JobsGateDotState {
  if (conclusion === "passed") return "pass";
  if (conclusion === "failed") return "fail";
  if (conclusion === "queued" || conclusion === "running") return "pending";
  return "none";
}

/**
 * ERC/DRC/BOM dots. Unknown / missing kinds stay gray (none).
 * A single overall pass must not light all three green.
 */
export function jobsGateDots(item: {
  check_conclusion?: CheckConclusion | string | null;
  check_kind?: string | null;
}): JobsGateDot[] {
  const hit = jobsGateKindFromCheck(item.check_kind);
  const state = jobsGateDotState(item.check_conclusion);
  return JOBS_GATE_KINDS.map((kind) => ({
    kind,
    state: hit === kind ? state : "none",
  }));
}

export function jobsGateDotClass(state: JobsGateDotState): string {
  if (state === "pass") return "bg-[#3D9B6A]";
  if (state === "fail") return "bg-[#E31C25]";
  if (state === "pending") return "bg-white/45 ring-1 ring-white/40";
  return "border border-white/25 bg-transparent";
}

export function jobsGateAriaLabel(dots: JobsGateDot[], overall: string): string {
  const parts = dots.map((dot) => `${dot.kind} ${dot.state}`).join(" · ");
  return `${overall}. ${parts}`;
}

export function changeAuthorLabel(createdBy: string | null | undefined): string {
  const v = createdBy?.trim();
  if (!v) return "—";
  if (v.includes("@")) return v.slice(0, v.indexOf("@")) || v;
  return v.length > 12 ? `${v.slice(0, 10)}…` : v;
}

export function relativeChangeTime(iso: string | null | undefined, now = Date.now()): string {
  const raw = iso?.trim();
  if (!raw) return "—";
  const t = Date.parse(raw);
  if (!Number.isFinite(t)) return raw;
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}d`;
  return new Date(t).toISOString().slice(0, 10);
}

export function changeSnapshotOptions(revisions: HardwareRevision[]): ChangeSnapshotOption[] {
  return revisions.map((rev) => {
    const oid = trimRef(rev.git_oid);
    const name = trimRef(rev.label) || rev.id;
    const oidBit = oid ? shortOid(oid) : null;
    return {
      snapshot: rev.id,
      oid,
      label: oidBit && oidBit !== "—" ? `${name} ${oidBit}` : name,
    };
  });
}

/** Hardware list is newest-first; baseline is the older snapshot. */
export function defaultSnapshotPair(snapshots: ChangeSnapshotOption[]): { baseline: string; candidate: string } {
  if (snapshots.length === 0) return { baseline: "", candidate: "" };
  return {
    baseline: snapshots[snapshots.length - 1]!.snapshot,
    candidate: snapshots[0]!.snapshot,
  };
}

export function newChangeBody(input: {
  baseline: ChangeSnapshotOption;
  candidate: ChangeSnapshotOption;
  title?: string;
}): {
  baseline_snapshot: string;
  candidate_snapshot: string;
  baseline_oid?: string;
  candidate_oid?: string;
  title?: string;
} {
  const title = input.title?.trim();
  return {
    baseline_snapshot: input.baseline.snapshot,
    candidate_snapshot: input.candidate.snapshot,
    ...(input.baseline.oid ? { baseline_oid: input.baseline.oid } : {}),
    ...(input.candidate.oid ? { candidate_oid: input.candidate.oid } : {}),
    ...(title ? { title } : {}),
  };
}

export function changeHref(
  org: string,
  project: string,
  item: Pick<ChangeListItem, "id" | "baseline_snapshot" | "candidate_snapshot">,
): string {
  const qs = new URLSearchParams({
    from_ref: item.baseline_snapshot,
    to_ref: item.candidate_snapshot,
  });
  return `/o/${org}/p/${project}/changes/${item.id}?${qs.toString()}`;
}

export function changeRowMeta(item: ChangeListItem): {
  title: string;
  oidRange: string;
  statusLabel: string;
  statusTone: ChangeStatusTone;
  statusClass: string;
  dots: JobsGateDot[];
  checksLabel: string;
  author: string;
  when: string;
} {
  const statusTone = changeStatusTone(item.review_status);
  return {
    title: changeTitle(item),
    oidRange: changeOidRange(item),
    statusLabel: reviewStatusLabel(item.review_status),
    statusTone,
    statusClass: changeStatusPillClass(statusTone),
    dots: jobsGateDots(item),
    checksLabel: checkConclusionLabel(item.check_conclusion),
    author: changeAuthorLabel(item.created_by),
    when: relativeChangeTime(item.created_at),
  };
}
